import { ReactNode } from 'react';
import { classNames } from 'primereact/utils';

interface InfoTooltipIconProps {
  /** Shown in the hover tooltip — plain text, or richer markup. */
  tooltip: ReactNode;
  /**
   * Which edge of the icon the fixed-width tooltip hangs from. Defaults to
   * `left` (opens rightwards), which suits an icon sitting after a label. Pass
   * `right` when the icon is near the right edge of its container — the
   * tooltip still takes part in layout while hidden, so a left-anchored one
   * there sticks out and gives the container a horizontal scrollbar.
   */
  align?: 'left' | 'right';
  /** Panel sizing, set by the consumer; defaults to the narrow single-note width. */
  panelClassName?: string;
}

// Generic info-icon-with-hover-tooltip. Mirrors the ValidationErrorIcon
// pattern (CSS-only group-hover tooltip, no portal needed) but renders a
// neutral info circle instead of the red warning triangle. Use anywhere a
// short clarifying note belongs next to a heading or label.
export const InfoTooltipIcon = ({ tooltip, align = 'left', panelClassName }: InfoTooltipIconProps) => {
  return (
    <div className="group relative mt-px flex cursor-pointer items-center justify-center self-center leading-none">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-fletchers-gray-200"
        aria-hidden
      >
        <circle cx={12} cy={12} r={10} />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
      <div
        className={classNames(
          'pointer-events-none invisible absolute bottom-full z-[999999] mb-1 rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm whitespace-pre-line text-black opacity-0 shadow-xl transition-opacity duration-100 group-hover:visible group-hover:opacity-100',
          align === 'right' ? 'right-0' : 'left-0',
          // Last so a consumer's width wins over the default.
          panelClassName ?? 'w-[220px]',
        )}
      >
        {tooltip}
      </div>
    </div>
  );
};
